import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Icon from 'assets/components/icon/Icon';
import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { join, toUpper } from 'lodash';
import {
  ArithmeticOperators,
  AutomationTokenTypes,
  BracketTokens,
  LogicalOperators,
  RelationalOperators,
} from 'models/enums/Automation';
import { AutomationRuleLike, AutomationRuleSchema } from '../helpers/AutomationTypes';
import {
  addExpression,
  deleteExpression,
  deleteLayoutToken,
  getExpressionByReference,
  insertArithmeticOperatorToken,
  insertBracketToken,
  insertExpressionAtLayoutIndex,
  insertLogicalToken,
  insertRelationalOperatorToken,
} from '../helpers/ruleHelpers';
import AutomationExpressionEditor from './Automation.Expression.Editor';
import AutomationEffectEditor from './Automation.Effect.Editor';
import AutomationReadonlySummary from './Automation.ReadonlySummary';

type Props = {
  rule: AutomationRuleLike;
  schema: AutomationRuleSchema;
  readonly?: boolean;
  hideDeleteRule?: boolean;
  onChange(rule: AutomationRuleLike): void;
  onDeleteRule?(): void;
};

export default function AutomationRuleRow(props: Props) {
  const { lang, translate } = getLocales();
  const canAddExpression =
    props.schema.allowMultipleExpressions && props.schema.expressionProperties.length > 0 && !props.readonly;
  const isExpressionMode = Boolean(props.schema.allowMultipleExpressions);
  const layoutTokens = props.rule.logicalLayout?.length
    ? props.rule.logicalLayout
    : (props.rule.expressions || []).map(
        (expression) =>
          ({
            type: AutomationTokenTypes.Expression,
            referenceId: expression.uid,
          }) as Model.Automation.ILayoutToken
      );
  const hasExpressionRowActions = !isExpressionMode && Boolean(props.schema.allowMultipleExpressions);
  const hasRowActions = isExpressionMode || hasExpressionRowActions;
  const showFooterAddExpression = !isExpressionMode && canAddExpression;
  const showFooterDeleteRule = !props.hideDeleteRule && Boolean(props.onDeleteRule);
  const hasFooterActions = showFooterAddExpression || showFooterDeleteRule;
  const columnCount = hasRowActions ? 3 : 2;

  if (props.readonly || props.schema.readonly) {
    return <AutomationReadonlySummary rule={props.rule} schema={props.schema} />;
  }

  const removeExpression = (expressionUid: string) => {
    const nextRule = deleteExpression(props.rule, expressionUid);
    if (!nextRule.expressions?.length && props.onDeleteRule) {
      props.onDeleteRule();
      return;
    }

    props.onChange(nextRule);
  };

  const getExpressionItemGroups = (layoutIndex: number) => [
    {
      id: 'logical',
      items: [
        {
          label: translate(LogicalOperators.And),
          description: lang.addAndOperatorDescription,
          insert: () => insertLogicalToken(props.rule, layoutIndex, LogicalOperators.And),
        },
        {
          label: translate(LogicalOperators.Or),
          description: lang.addOrOperatorDescription,
          insert: () => insertLogicalToken(props.rule, layoutIndex, LogicalOperators.Or),
        },
        {
          label: translate(LogicalOperators.Not),
          description: lang.addNotOperatorDescription,
          insert: () => insertLogicalToken(props.rule, layoutIndex, LogicalOperators.Not),
        },
      ],
    },
    {
      id: 'arithmetic',
      items: Object.values(ArithmeticOperators).map((operator) => ({
        label: operator,
        description: lang.arithmeticOperator,
        insert: () => insertArithmeticOperatorToken(props.rule, layoutIndex, operator),
      })),
    },
    {
      id: 'relational',
      items: Object.values(RelationalOperators).map((operator) => ({
        label: operator,
        description: lang.relationalOperator,
        insert: () => insertRelationalOperatorToken(props.rule, layoutIndex, operator),
      })),
    },
    {
      id: 'bracket',
      items: [
        {
          label: BracketTokens.OpenBracket,
          description: lang.addOpenBracketDescription,
          insert: () => insertBracketToken(props.rule, layoutIndex, BracketTokens.OpenBracket),
        },
        {
          label: BracketTokens.CloseBracket,
          description: lang.addCloseBracketDescription,
          insert: () => insertBracketToken(props.rule, layoutIndex, BracketTokens.CloseBracket),
        },
      ],
    },
  ];

  const renderInsertDropdown = (layoutIndex: number, label: string) => {
    const expressionItemGroups = getExpressionItemGroups(layoutIndex);

    return (
      <DropdownButton
        subcomponents={ThemeDropdownButtonPopup}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        title={label}
        htmlElementProps={{ title: label }}
        buttonMedia={<Icon class="fas-plus" />}
        dropdownOptions={(animation) => (
          <>
            {canAddExpression && (
              <Button
                class={ThemeButtonMenuItem}
                media={<Icon class="fas-plus" />}
                render={
                  <>
                    <span>{toUpper(lang.expression)}</span>
                    <span>{lang.addExpressionHere}</span>
                  </>
                }
                onClick={() => {
                  props.onChange(insertExpressionAtLayoutIndex(props.rule, props.schema, layoutIndex));
                  animation.animateOut();
                }}
                htmlElementProps={{ title: lang.addExpression }}
              />
            )}
            {expressionItemGroups.flatMap((group) =>
              group.items.map((item) => (
                <Button
                  key={`${group.id}_${item.label}`}
                  class={ThemeButtonMenuItem}
                  media={<Icon class="fas-plus" />}
                  render={
                    <>
                      <span>{toUpper(item.label)}</span>
                      <span>{item.description}</span>
                    </>
                  }
                  onClick={() => {
                    props.onChange(item.insert());
                    animation.animateOut();
                  }}
                  htmlElementProps={{ title: join([lang.add, item.label], ' ') }}
                />
              ))
            )}
          </>
        )}
      />
    );
  };

  const renderLayoutToken = (token: Model.Automation.ILayoutToken) => {
    if (token.type !== AutomationTokenTypes.Expression) {
      const label =
        token.type === AutomationTokenTypes.LogicalOperator ? translate(token.referenceId) : token.referenceId;
      return <strong>{toUpper(label)}</strong>;
    }

    const expression = getExpressionByReference(props.rule, token.referenceId);
    if (!expression) return null;

    return (
      <AutomationExpressionEditor
        rule={props.rule}
        expression={expression}
        schema={props.schema}
        hidePrefix
        onChange={props.onChange}
      />
    );
  };

  const getTokenTypeLabel = (token: Model.Automation.ILayoutToken) => {
    if (token.type === AutomationTokenTypes.Expression) return lang.expression;
    if (
      token.type === AutomationTokenTypes.LogicalOperator ||
      token.type === AutomationTokenTypes.RelationalOperator ||
      token.type === AutomationTokenTypes.ArithmeticOperator
    ) {
      return lang.operator;
    }
    return lang.bracket;
  };

  return (
    <table className={`${ThemeTableSimple2} `} style={{ tableLayout: 'fixed', width: '100%' }}>
      <thead>
        <tr>
          <th style={{ width: '12.5%' }}>{lang.type}</th>
          <th style={{ width: 'auto' }}>{lang.expressionItem}</th>
          {hasRowActions && <th style={{ width: '10%' }}>{lang.actions}</th>}
        </tr>
      </thead>
      <tbody>
        {isExpressionMode ? (
          <>
            <tr>
              <td style={{ width: '12.5%' }}>{lang.start}</td>
              <td style={{ width: 'auto' }}>{lang.insertAtStart}</td>
              <td style={{ width: '10%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  {renderInsertDropdown(0, lang.insertAtStart)}
                </div>
              </td>
            </tr>
            {layoutTokens.map((token, index) => (
              <tr key={`${token.type}_${token.referenceId}_${index}`}>
                <td style={{ width: '12.5%' }}>{getTokenTypeLabel(token)}</td>
                <td style={{ width: 'auto' }}>{renderLayoutToken(token)}</td>
                <td style={{ width: '10%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    {renderInsertDropdown(index + 1, lang.insertAfter)}
                    <Button
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-trash" />}
                      onClick={() => props.onChange(deleteLayoutToken(props.rule, index))}
                      htmlElementProps={{ title: lang.remove }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </>
        ) : (
          (props.rule.expressions || []).map((expression) => (
            <tr key={expression.uid}>
              <td style={{ width: '12.5%' }}>{lang.expression}</td>
              <td style={{ width: 'auto' }}>
                <AutomationExpressionEditor
                  rule={props.rule}
                  expression={expression}
                  schema={props.schema}
                  onChange={props.onChange}
                />
              </td>
              {hasExpressionRowActions && (
                <td style={{ width: '10%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <Button
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-trash" />}
                      onClick={() => removeExpression(expression.uid)}
                    />
                  </div>
                </td>
              )}
            </tr>
          ))
        )}
        {props.schema.effects.map((effect, index) => (
          <tr key={effect.type === 'assign' ? effect.propertyPath : effect.propertyPathPrefix}>
            <td style={{ width: '12.5%' }}>{lang.effect}</td>
            <td style={{ width: 'auto' }} colSpan={hasRowActions ? 2 : 1}>
              <AutomationEffectEditor
                rule={props.rule}
                effect={effect}
                effectIndex={index}
                schema={props.schema}
                onChange={props.onChange}
              />
            </td>
          </tr>
        ))}
      </tbody>
      {hasFooterActions && (
        <tfoot>
          <tr>
            <td colSpan={columnCount}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                {showFooterAddExpression && (
                  <Button
                    class={ThemeButtonCircle}
                    render={lang.addExpression}
                    active
                    media={<Icon class="fas-plus" />}
                    onClick={() => props.onChange(addExpression(props.rule, props.schema))}
                  />
                )}
                {showFooterDeleteRule && props.onDeleteRule && (
                  <Button
                    class={ThemeButtonCircle}
                    media={<Icon class="fas-trash" />}
                    render={lang.deleteRule}
                    onClick={props.onDeleteRule}
                    active
                  />
                )}
              </div>
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}
